// Fallback for funds TMX Money cannot price (see market/tmx.ts): asks the user's LLM, with web search on, to find
// the latest NAV for a fund code and answer as JSON. Verified 2026-09-17: fundlibrary.com, ca.investing.com and
// tools.bmogam.com carry the NAV in page text; Google Finance and theglobeandmail.com render it with scripts and
// are not readable this way.
import { llmConfig } from '@/experts/run';
import { t } from '@/i18n';
import type { ChatMeta } from '@/llm/types';
import { todayNY } from '@/market/marketHours';
import { actualCost } from '@/reports/cost';
import { settingsStore } from '@/settings/settingsStore';
import { appStore } from '@/state/appStore';
import type { Asset } from '@/types';

export interface NavLookup {
  nav: number;
  currency?: string;
  asOf: string; // YYYY-MM-DD
  source?: string;
  name?: string;
  searches: number;
  costUsd: number | null;
}

const MAX_AGE_DAYS = 10;

const SYSTEM = `你是基金净值核对助手。任务：用搜索工具找到指定共同基金最新公布的单位净值（NAV）。

规则：
- 只使用本次搜索到的网页内容；不得使用记忆中的数字。
- 用基金代码（如 "FID5982"）和基金名称搜索。已知可读的来源：fundlibrary.com 的基金详情页、ca.investing.com 的基金页、tools.bmogam.com、基金公司官网；theglobeandmail.com 与 Google Finance 的价格是脚本加载的，常常读不到。
- 必须同时取得净值日期；日期不明确的数字不要用。
- 注意系列：代码对应的系列（如 Series F）与同一基金其他系列的净值不同，不要混用。
- 只输出一个 JSON 对象，不要任何其他文字：
{"nav": 69.76, "currency": "CAD", "asOf": "2026-09-16", "source": "https://…", "name": "基金全名"}
找不到时输出：{"nav": null, "reason": "原因"}`;

/** Pure; exported for tests. Finds the JSON object in the model's answer and validates it. */
export function parseNavAnswer(text: string): { nav: number | null; currency?: string; asOf?: string; source?: string; name?: string; reason?: string } | null {
  const m = text.match(/\{[\s\S]*\}/);
  if (!m) return null;
  try {
    const j = JSON.parse(m[0]) as { nav?: unknown; currency?: unknown; asOf?: unknown; source?: unknown; name?: unknown; reason?: unknown };
    if (j.nav == null) return { nav: null, reason: typeof j.reason === 'string' ? j.reason : undefined };
    const nav = Number(j.nav);
    if (!Number.isFinite(nav) || nav <= 0) return { nav: null, reason: 'invalid' };
    const asOf = typeof j.asOf === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(j.asOf) ? j.asOf : undefined;
    if (!asOf) return { nav: null, reason: 'no date' };
    return {
      nav,
      asOf,
      currency: typeof j.currency === 'string' && j.currency ? j.currency.toUpperCase() : undefined,
      source: typeof j.source === 'string' ? j.source : undefined,
      name: typeof j.name === 'string' ? j.name : undefined,
    };
  } catch {
    return null;
  }
}

/** Whether a looked-up NAV is safe to apply without a human check: right currency and published recently. */
export function navAcceptable(r: Pick<NavLookup, 'currency' | 'asOf'>, currency: string, today = todayNY()): boolean {
  if (r.currency && r.currency !== currency.toUpperCase()) return false;
  const age = (Date.parse(`${today}T00:00:00Z`) - Date.parse(`${r.asOf}T00:00:00Z`)) / 86_400_000;
  return Number.isFinite(age) && age >= 0 && age <= MAX_AGE_DAYS;
}

/** Timestamp for a NAV date: end of that day in New York, so the list shows the NAV date, not the lookup time. */
export function navTimestamp(asOf: string): number {
  return Date.parse(`${asOf}T21:00:00Z`);
}

export async function lookupFundNav(fund: Pick<Asset, 'symbol' | 'name' | 'market' | 'currency'>): Promise<NavLookup> {
  const cfg = llmConfig();
  const user = [
    `基金代码：${fund.symbol.toUpperCase()}`,
    `名称（可能不完整）：${fund.name}`,
    `市场：${fund.market === 'CA' ? '加拿大（FundSERV 代码）' : '美国'}`,
    `预期币种：${fund.currency.toUpperCase()}`,
    `今天：${todayNY()}`,
  ].join('\n');
  let meta: ChatMeta = {};
  const text = await cfg.adapter.chat({
    system: SYSTEM,
    messages: [{ role: 'user', content: [{ type: 'text', text: user }] }],
    model: cfg.model,
    apiKey: cfg.apiKey,
    webSearch: { maxUses: 4 },
    onMeta: (m) => {
      meta = m;
    },
  });
  const parsed = parseNavAnswer(text);
  if (!parsed) throw new Error(t('nav.unparsable'));
  if (parsed.nav == null || !parsed.asOf) throw new Error(t('nav.notFound', { reason: parsed.reason ?? '' }));
  const provider = settingsStore.get().llm.activeProvider;
  return {
    nav: parsed.nav,
    asOf: parsed.asOf,
    currency: parsed.currency,
    source: parsed.source,
    name: parsed.name,
    searches: meta.searches ?? 0,
    costUsd: actualCost(provider, cfg.model, { inputTokens: meta.inputTokens, outputTokens: meta.outputTokens, searches: meta.searches }),
  };
}

/** Applies a looked-up NAV to a fund holding; fills in the fund name when the holding still carries the bare code. */
export async function applyNav(asset: Asset, r: NavLookup): Promise<void> {
  const changes: Partial<Asset> = { manualPrice: r.nav, manualPriceAt: navTimestamp(r.asOf) };
  if (r.name && (asset.name === asset.symbol || asset.name === asset.symbol.toUpperCase())) changes.name = r.name;
  await appStore.updateAsset(asset.id, changes);
}
